import { Box, Menu, Popover, TextInput } from '@mantine/core';
import IconArrowsJoin from '@tabler/icons-react/dist/esm/icons/IconArrowsJoin.mjs';
import IconArrowsSplit from '@tabler/icons-react/dist/esm/icons/IconArrowsSplit.mjs';
import IconEdit from '@tabler/icons-react/dist/esm/icons/IconEdit.mjs';
import IconLayersDifference from '@tabler/icons-react/dist/esm/icons/IconLayersDifference.mjs';
import IconLayersSubtract from '@tabler/icons-react/dist/esm/icons/IconLayersSubtract.mjs';
import IconTrash from '@tabler/icons-react/dist/esm/icons/IconTrash.mjs';
import { useTranslation } from 'react-i18next';
import { GraphGroupMenuItems } from './group-menu-items';
import { hasGraphClass, UNAVAILABLE_CLASS } from '@/shared/ui/graph-renderer';
import type {
  GraphRendererApi,
  GraphNodeDefinition,
} from '@/shared/ui/graph-renderer';

export const GraphViewNodeMenu = ({
  api,
  node,
  close,
}: {
  api: GraphRendererApi;
  node: GraphNodeDefinition;
  close: () => void;
}) => {
  const { t } = useTranslation();
  const views = api.views;
  if (!views) return null;
  const id = node.data.id;
  const state = views.getNodeState(id);
  const leafActions = !state.isGroup || state.isCollapsed;
  const action = (run: () => void) => () => {
    run();
    close();
  };
  return (
    <Box data-testid='node-context-menu-container'>
      {state.isGroup && !state.isCollapsed && (
        <Menu.Item
          data-testid='ungroup-menu-item'
          leftSection={<IconLayersSubtract />}
          onClick={action(() => views.ungroup([id]))}
        >
          {t('graphViews.actions.ungroup', { ns: 'srm' })}
        </Menu.Item>
      )}
      <GraphGroupMenuItems api={api} nodeId={id} close={close} />
      {leafActions && state.canCollapseEdges && (
        <Menu.Item
          data-testid='collapse-edges-menu-item'
          leftSection={<IconArrowsJoin />}
          onClick={action(() => views.collapseConnectedEdges(id))}
        >
          {`${t('graphViews.actions.collapse', { ns: 'srm' })} ${t('graphViews.text.edges', { ns: 'srm' }).toLocaleLowerCase()}`}
        </Menu.Item>
      )}
      {leafActions && state.canExpandEdges && (
        <Menu.Item
          data-testid='expand-edges-menu-item'
          leftSection={<IconArrowsSplit />}
          onClick={action(() => views.expandConnectedEdges(id))}
        >
          {`${t('graphViews.actions.expand', { ns: 'srm' })} ${t('graphViews.text.edges', { ns: 'srm' }).toLocaleLowerCase()}`}
        </Menu.Item>
      )}
      {leafActions && !state.isCollapsed && state.canConvert && (
        <Menu.Item
          data-testid='convert-to-compound-menu-item'
          leftSection={<IconLayersDifference />}
          onClick={action(() => views.convertToGroup(id))}
        >
          {t('graphViews.actions.convertToCompound', { ns: 'srm' })}
        </Menu.Item>
      )}
      <Popover withinPortal={false}>
        <Popover.Target>
          <Menu.Item leftSection={<IconEdit />} data-testid='rename-menu-item'>
            {t('action.rename')}
          </Menu.Item>
        </Popover.Target>
        <Popover.Dropdown>
          <TextInput
            data-testid='rename-node-input'
            defaultValue={node.data.ci_name}
            onChange={(event) =>
              api.updateNode(id, {
                data: { ci_name: event.currentTarget.value },
              })
            }
          />
        </Popover.Dropdown>
      </Popover>
      {hasGraphClass(node.classes, UNAVAILABLE_CLASS) && (
        <Menu.Item
          color='red'
          data-testid='remove-menu-item'
          leftSection={<IconTrash />}
          onClick={action(() => views.removeUnavailable([id]))}
        >
          {t('graphViews.actions.remove', { ns: 'srm' })}
        </Menu.Item>
      )}
    </Box>
  );
};
